import { useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import axios from "axios";
import url, { getFullURL } from "@/app/api";
import { isUserAuthenticated } from "@/app/selectors/user";
import { processError, useTranslateText } from "@/app/utils";
import {
  CheckInError,
  toCheckInError,
} from "@/app/components/PickupDriveThru/checkInError";

type CustomerArrivalParams = {
  orderId: string;
  backupPin: string;
};

const toCustomerArrivalError = (err: unknown): CheckInError => {
  if (!axios.isAxiosError(err)) {
    console.error("Customer arrival error:", err);
    return { kind: "unknown" };
  }
  if (!err.response) {
    return { kind: "networkError" };
  }
  const apiError = err.response.data
    ? processError(err)
    : { message: "", errors: [] };
  return toCheckInError({ ...apiError, httpStatus: err.response.status });
};

export function useCustomerArrival() {
  const authToken = useSelector(isUserAuthenticated);
  const { locale } = useTranslateText();

  return useMutation<void, CheckInError, CustomerArrivalParams>({
    mutationFn: async ({ orderId, backupPin }) => {
      if (!authToken) {
        throw { kind: "unauthorized" } satisfies CheckInError;
      }

      const endpoint = url.customerArrival(orderId);
      try {
        await axios.post(
          getFullURL(endpoint.url, endpoint.prefix, endpoint.apiVersion),
          { customerArrival: { backupPin } },
          {
            headers: {
              "Content-Type": "application/json",
              "ALBAIK-Auth-Token": authToken,
              "Accept-Language": locale,
            },
          },
        );
      } catch (err) {
        throw toCustomerArrivalError(err);
      }
    },
  });
}
